import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, DollarSign, Building2, Calendar, Eye, CheckCircle, XCircle, Clock, ShieldCheck, ShieldAlert } from "lucide-react";
import { collection, query, orderBy, onSnapshot, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useLanguage } from "@/contexts/LanguageContext";
import { LENDER_TYPES, LOAN_PRODUCT_TYPES, COLLATERAL_OPTIONS } from "@/constants/lender-offer.constants";

interface LenderOfferRow {
  id: string;
  deal_id: string | null;
  user_id: string;
  offer_data: any;
  status: string;
  lender_company_name: string;
  loan_amount: number;
  interest_rate: number;
  loan_term_months: number;
  lender_type: string | null;
  loan_product_type: string | null;
  created_at: string;
}

const T = {
  en: {
    title: "Lenders & Offers",
    subtitle: "Manage financing offers from lenders",
    pending: "Pending", approved: "Approved", rejected: "Rejected", all: "All",
    pendingOffers: "Pending Offers", totalValue: "Total Value",
    search: "Search lenders or deals...",
    viewDetails: "View Details", approve: "Approve", reject: "Reject",
    noOffers: "No offers found",
    loanType: "Loan Type & Terms", commercialTerms: "Commercial Terms",
    structuredFees: "Structured Fees", covenants: "Covenants, Ratios & Collateral",
    creditReqs: "Credit Requirements & International", timeline: "Timeline & Final Review",
    lenderType: "Lender Type", loanProduct: "Loan Product Type",
    loanAmount: "Loan Amount Offered", term: "Term (months)", rateType: "Rate Type",
    baseRate: "Base Rate (%)", margin: "Margin (bps)", amortization: "Amortization",
    floorRate: "Floor Rate (%)", prepayment: "Prepayment Penalty",
    arrangementFee: "Arrangement Fee (%)", drawFee: "ABL Draw Fee (%)", exitFee: "Exit Fee (%)",
    maintenanceCovenants: "Maintenance Covenants", netDebtEBITDA: "Net Debt/EBITDA Max",
    dscrMin: "DSCR Minimum", dscrBase: "DSCR Base", dividendRestrictions: "Dividend Restrictions",
    collateralRequired: "Collateral Required", collateralTypes: "Collateral Types",
    pgRequired: "Personal Guarantee Required", reConditioned: "RE Mortgage Conditioned",
    ablOffer: "ABL Offer", arEligible: "AR Eligible (%)", invEligible: "Inventory Eligible (%)",
    cashSweep: "Cash Sweep", subDebt: "Subordinated Debt", subToSenior: "Subordinated to Senior",
    gracePeriod: "Grace Period (months)", balloonYear: "Balloon Year", balloonAmount: "Balloon Amount",
    creditScore: "Credit Score Required", minScore: "Minimum Score",
    foreignCredit: "Foreign Credit Accepted", foreignMinScore: "Foreign Min Score",
    foreignCountries: "Accepted Countries", foreignCollateral: "Foreign Collateral",
    foreignCollateralTypes: "Foreign Collateral Types",
    closingDays: "Closing Timeline (days)", expiration: "Offer Expiration",
    yes: "Yes", no: "No", notProvided: "Not provided",
  },
  fr: {
    title: "Prêteurs & Offres",
    subtitle: "Gérer les offres de financement des prêteurs",
    pending: "En attente", approved: "Approuvée", rejected: "Rejetée", all: "Toutes",
    pendingOffers: "Offres en attente", totalValue: "Valeur totale",
    search: "Rechercher prêteurs ou deals...",
    viewDetails: "Voir Détails", approve: "Approuver", reject: "Rejeter",
    noOffers: "Aucune offre trouvée",
    loanType: "Type & Termes du Prêt", commercialTerms: "Termes Commerciaux",
    structuredFees: "Frais Structurés", covenants: "Covenants, Ratios & Garanties",
    creditReqs: "Exigences de Crédit & International", timeline: "Délais & Révision Finale",
    lenderType: "Type de Prêteur", loanProduct: "Type de Produit",
    loanAmount: "Montant Offert", term: "Durée (mois)", rateType: "Type de Taux",
    baseRate: "Taux de Base (%)", margin: "Marge (bps)", amortization: "Amortissement",
    floorRate: "Taux Plancher (%)", prepayment: "Pénalité Remboursement Anticipé",
    arrangementFee: "Frais d'Arrangement (%)", drawFee: "Frais Tirage ABL (%)", exitFee: "Frais de Sortie (%)",
    maintenanceCovenants: "Covenants de Maintenance", netDebtEBITDA: "Dette Nette/EBITDA Max",
    dscrMin: "DSCR Minimum", dscrBase: "Base DSCR", dividendRestrictions: "Restrictions Dividendes",
    collateralRequired: "Garanties Requises", collateralTypes: "Types de Garanties",
    pgRequired: "Garantie Personnelle Requise", reConditioned: "Conditionné Hypothèque",
    ablOffer: "Offre ABL", arEligible: "CC Éligibles (%)", invEligible: "Inventaire Éligible (%)",
    cashSweep: "Cash Sweep", subDebt: "Dette Subordonnée", subToSenior: "Subordonnée à Senior",
    gracePeriod: "Période Carence (mois)", balloonYear: "Année Ballon", balloonAmount: "Montant Ballon",
    creditScore: "Score de Crédit Requis", minScore: "Score Minimum",
    foreignCredit: "Crédit Étranger Accepté", foreignMinScore: "Score Étranger Min",
    foreignCountries: "Pays Acceptés", foreignCollateral: "Garanties Étrangères",
    foreignCollateralTypes: "Types Garanties Étrangères",
    closingDays: "Délai de Clôture (jours)", expiration: "Expiration de l'Offre",
    yes: "Oui", no: "Non", notProvided: "Non fourni",
  }
};

function SectionHeader({ title }: { title: string }) {
  return <h3 className="text-lg font-bold text-primary border-b pb-2 mt-6 mb-3">{title}</h3>;
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  if (value === null || value === undefined || value === '' || value === 0) return null;
  return (
    <div className="flex justify-between py-1.5 border-b border-muted last:border-0">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%]">{value}</span>
    </div>
  );
}

function OfferDetailDialog({ offer, open, onClose, lang }: { offer: LenderOfferRow | null; open: boolean; onClose: () => void; lang: string }) {
  const t = T[lang as 'en' | 'fr'] || T.en;
  const lTypes = LENDER_TYPES[lang as 'en' | 'fr'] || LENDER_TYPES.en;
  const pTypes = LOAN_PRODUCT_TYPES[lang as 'en' | 'fr'] || LOAN_PRODUCT_TYPES.en;
  const cOpts = COLLATERAL_OPTIONS[lang as 'en' | 'fr'] || COLLATERAL_OPTIONS.en;
  
  if (!offer) return null;
  const d = offer.offer_data || {};

  const rateTypeLabel = d.interestRateType === 'fixed' ? 'Fixed' : d.interestRateType === 'floating' ? 'Floating' : 'Indexed (SOFR+)';
  const amortLabel = d.amortizationType === 'linear' ? 'Linear' : d.amortizationType === 'in_fine' ? 'In Fine (Balloon)' : 'Custom';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>{offer.lender_company_name} — ${offer.loan_amount?.toLocaleString()}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[70vh] pr-4">
          {/* NDA — shown first, mandatory legal proof */}
          {(() => {
            const ndaOk = d.ndaAcknowledge === true && (d.ndaSignatureName || '').trim().length > 1;
            return (
              <div className={`rounded-lg border p-3 mb-4 ${ndaOk ? 'border-green-500/40 bg-green-500/10' : 'border-red-500/40 bg-red-500/10'}`}>
                <div className="flex items-center gap-2 font-semibold">
                  {ndaOk ? <ShieldCheck className="h-4 w-4 text-green-500" /> : <ShieldAlert className="h-4 w-4 text-red-500" />}
                  {ndaOk ? 'NDA Signed & Attached to Offer' : 'NDA Missing — offer cannot be processed'}
                </div>
                {ndaOk && (
                  <div className="text-xs mt-2 space-y-0.5 opacity-80">
                    <div>Signed by: <strong>{d.ndaSignatureName}</strong>{d.ndaSignatureTitle ? ` — ${d.ndaSignatureTitle}` : ''}</div>
                    <div>Signed at: {offer.created_at ? new Date(offer.created_at).toLocaleString() : '—'}</div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Loan Type & Terms */}
          <SectionHeader title={t.loanType} />
          <InfoRow label={t.lenderType} value={lTypes[d.lenderType as keyof typeof lTypes] || d.lenderType} />
          <InfoRow label={t.loanProduct} value={pTypes[d.loanProductType as keyof typeof pTypes] || d.loanProductType} />

          {/* Commercial Terms */}
          <SectionHeader title={t.commercialTerms} />
          <InfoRow label={t.loanAmount} value={`$${Number(d.loanAmountOffered || 0).toLocaleString()}`} />
          <InfoRow label={t.term} value={d.loanTermLengthMonths} />
          <InfoRow label={t.rateType} value={rateTypeLabel} />
          <InfoRow label={t.baseRate} value={d.interestRateValue ? `${d.interestRateValue}%` : null} />
          <InfoRow label={t.margin} value={d.interestRateMarginBps} />
          <InfoRow label={t.amortization} value={amortLabel} />
          <InfoRow label={t.floorRate} value={d.interestRateFloor ? `${d.interestRateFloor}%` : null} />
          <InfoRow label={t.prepayment} value={d.prepaymentPenalty || null} />

          {/* Structured Fees */}
          {(d.arrangementFeePercent || d.drawFeePercentABL || d.exitFeePercent) && (
            <>
              <SectionHeader title={t.structuredFees} />
              <InfoRow label={t.arrangementFee} value={d.arrangementFeePercent ? `${d.arrangementFeePercent}%` : null} />
              <InfoRow label={t.drawFee} value={d.drawFeePercentABL ? `${d.drawFeePercentABL}%` : null} />
              <InfoRow label={t.exitFee} value={d.exitFeePercent ? `${d.exitFeePercent}%` : null} />
            </>
          )}

          {/* Covenants, Ratios & Collateral */}
          <SectionHeader title={t.covenants} />
          <InfoRow label={t.maintenanceCovenants} value={d.maintenanceCovenantsRequired ? t.yes : t.no} />
          {d.maintenanceCovenantsRequired && (
            <>
              <InfoRow label={t.netDebtEBITDA} value={d.netDebtToEBITDAMaxAllowed ? `${d.netDebtToEBITDAMaxAllowed}x` : null} />
              <InfoRow label={t.dscrMin} value={d.dscrMinimumRequired ? `${d.dscrMinimumRequired}x` : null} />
              <InfoRow label={t.dscrBase} value={d.dscrCalculationBase?.toUpperCase()} />
              <InfoRow label={t.dividendRestrictions} value={d.dividendRestrictions || null} />
            </>
          )}
          <InfoRow label={t.collateralRequired} value={d.collateralRequired ? t.yes : t.no} />
          {d.collateralRequired && d.collateralRequirements?.length > 0 && (
            <InfoRow label={t.collateralTypes} value={
              d.collateralRequirements.map((k: string) => cOpts[k as keyof typeof cOpts] || k).join(', ')
            } />
          )}
          <InfoRow label={t.pgRequired} value={d.personalGuaranteeRequired ? t.yes : t.no} />
          <InfoRow label={t.reConditioned} value={d.realEstateConditioned ? t.yes : t.no} />
          
          {/* ABL */}
          {d.isABLOffer && (
            <>
              <InfoRow label={t.ablOffer} value={t.yes} />
              <InfoRow label={t.arEligible} value={d.ABL_AR_EligiblityPercent ? `${d.ABL_AR_EligiblityPercent}%` : null} />
              <InfoRow label={t.invEligible} value={d.ABL_Inventory_EligiblityPercent ? `${d.ABL_Inventory_EligiblityPercent}%` : null} />
              <InfoRow label={t.cashSweep} value={d.ABL_Cash_Sweep ? t.yes : t.no} />
            </>
          )}
          
          {/* Subordinated */}
          {d.isSubordinatedDebt && (
            <>
              <InfoRow label={t.subDebt} value={t.yes} />
              <InfoRow label={t.subToSenior} value={d.subordinationToSeniorDebt ? t.yes : t.no} />
              <InfoRow label={t.gracePeriod} value={d.interestGracePeriodMonths} />
              <InfoRow label={t.balloonYear} value={d.balloonPaymentYear} />
              <InfoRow label={t.balloonAmount} value={d.balloonPaymentAmount ? `$${Number(d.balloonPaymentAmount).toLocaleString()}` : null} />
            </>
          )}

          {/* Credit Requirements & International */}
          <SectionHeader title={t.creditReqs} />
          <InfoRow label={t.creditScore} value={d.creditScoreRequired ? t.yes : t.no} />
          {d.creditScoreRequired && (
            <InfoRow label={t.minScore} value={d.creditScoreMinimum} />
          )}
          <InfoRow label={t.foreignCredit} value={d.acceptForeignCreditScores ? t.yes : t.no} />
          {d.acceptForeignCreditScores && (
            <>
              <InfoRow label={t.foreignMinScore} value={d.foreignCreditScoreMinimum} />
              {d.acceptedForeignCreditCountries?.length > 0 && (
                <InfoRow label={t.foreignCountries} value={d.acceptedForeignCreditCountries.join(', ')} />
              )}
            </>
          )}
          <InfoRow label={t.foreignCollateral} value={d.acceptForeignCollateral ? t.yes : t.no} />
          {d.acceptForeignCollateral && d.foreignCollateralRequirements?.length > 0 && (
            <InfoRow label={t.foreignCollateralTypes} value={
              d.foreignCollateralRequirements.map((k: string) => cOpts[k as keyof typeof cOpts] || k).join(', ')
            } />
          )}

          {/* Timeline */}
          <SectionHeader title={t.timeline} />
          <InfoRow label={t.closingDays} value={d.estimatedClosingTimelineDays} />
          <InfoRow label={t.expiration} value={d.offerExpirationDate} />
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default function LendersOffers() {
  const { language } = useLanguage();
  const t = T[language as 'en' | 'fr'] || T.en;
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [offers, setOffers] = useState<LenderOfferRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState<LenderOfferRow | null>(null);

 // ✅ Remplacer tout le useEffect par :
useEffect(() => {
  const q = query(collection(db, "lenderOffers"), orderBy("createdAt", "desc"));
  const unsub = onSnapshot(q, snap => {
    setOffers(snap.docs.map(d => ({ id: d.id, ...d.data(),
      lender_company_name: d.data().lenderCompanyName || "",
      loan_amount: d.data().loanAmount || 0,
      interest_rate: d.data().interestRate || 0,
      loan_term_months: d.data().loanTermMonths || 0,
      lender_type: d.data().lenderType || null,
      loan_product_type: d.data().loanProductType || null,
      deal_id: d.data().dealId || null,
      user_id: d.data().userId || "",
      created_at: d.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      status: d.data().status || "submitted",
    })) as any);
    setLoading(false);
  });
  return () => unsub();
}, []);

const updateStatus = async (offerId: string, newStatus: string) => {
  await updateDoc(doc(db, "lenderOffers", offerId), { status: newStatus, updatedAt: serverTimestamp() });
};

  const filteredOffers = offers.filter((offer) => {
    const matchesSearch =
      offer.lender_company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || offer.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      submitted: "bg-yellow-500/20 text-yellow-400 border-yellow-500/50",
      approved: "bg-green-500/20 text-green-400 border-green-500/50",
      rejected: "bg-red-500/20 text-red-400 border-red-500/50",
    };
    return styles[status] || "bg-blue-500/20 text-blue-400 border-blue-500/50";
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">{t.title}</h1>
        <p className="text-white/60 mt-1">{t.subtitle}</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-yellow-500/20"><Clock className="h-6 w-6 text-yellow-400" /></div>
              <div>
                <p className="text-sm text-white/60">{t.pendingOffers}</p>
                <p className="text-2xl font-bold text-white">{offers.filter(o => o.status === 'submitted').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-500/20"><CheckCircle className="h-6 w-6 text-green-400" /></div>
              <div>
                <p className="text-sm text-white/60">{t.approved}</p>
                <p className="text-2xl font-bold text-white">{offers.filter(o => o.status === 'approved').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white/10 border-white/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-blue-500/20"><Building2 className="h-6 w-6 text-blue-400" /></div>
              <div>
                <p className="text-sm text-white/60">{t.totalValue}</p>
                <p className="text-2xl font-bold text-white">${offers.reduce((s, o) => s + Number(o.loan_amount || 0), 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input placeholder={t.search} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40" />
        </div>
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList className="bg-white/10">
            <TabsTrigger value="all" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">{t.all}</TabsTrigger>
            <TabsTrigger value="submitted" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">{t.pending}</TabsTrigger>
            <TabsTrigger value="approved" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">{t.approved}</TabsTrigger>
            <TabsTrigger value="rejected" className="data-[state=active]:bg-brand-gold data-[state=active]:text-brand-navy">{t.rejected}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Offers List */}
      <div className="space-y-4">
        {filteredOffers.length === 0 ? (
          <div className="text-center py-12 text-white/60">{t.noOffers}</div>
        ) : filteredOffers.map((offer) => (
          <Card key={offer.id} className="bg-white/10 border-white/20 hover:bg-white/15 transition-colors">
            <CardContent className="p-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <h3 className="text-lg font-semibold text-white">{offer.lender_company_name || 'Unknown'}</h3>
                    <Badge variant="outline" className={getStatusBadge(offer.status)}>
                      {offer.status === 'submitted' ? t.pending : offer.status === 'approved' ? t.approved : t.rejected}
                    </Badge>
                    {(() => {
                      const d: any = (offer as any).offer_data || {};
                      const ndaOk = d.ndaAcknowledge === true && (d.ndaSignatureName || '').trim().length > 1;
                      return ndaOk ? (
                        <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/50 gap-1">
                          <ShieldCheck className="h-3 w-3" /> NDA Signed
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-500/20 text-red-400 border-red-500/50 gap-1">
                          <ShieldAlert className="h-3 w-3" /> NDA Missing
                        </Badge>
                      );
                    })()}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2 text-white/70">
                      <DollarSign className="h-4 w-4" />${Number(offer.loan_amount).toLocaleString()}
                    </div>
                    <div className="flex items-center gap-2 text-white/70">
                      <span className="font-medium">{offer.interest_rate}%</span> APR
                    </div>
                    <div className="flex items-center gap-2 text-white/70">
                      <Calendar className="h-4 w-4" />{offer.loan_term_months} months
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="border-white/20 text-white hover:bg-white/10" onClick={() => setSelectedOffer(offer)}>
                    <Eye className="h-4 w-4 mr-2" />{t.viewDetails}
                  </Button>
                  {offer.status === "submitted" && (
                    <>
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => updateStatus(offer.id, 'approved')}>
                        <CheckCircle className="h-4 w-4 mr-2" />{t.approve}
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => updateStatus(offer.id, 'rejected')}>
                        <XCircle className="h-4 w-4 mr-2" />{t.reject}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <OfferDetailDialog offer={selectedOffer} open={!!selectedOffer} onClose={() => setSelectedOffer(null)} lang={language} />
    </div>
  );
}
